import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import { ArrowLeft, Edit, Plus, PlusSquare } from 'lucide-react';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import CustomFieldCard from '../CustomFieldCard';
import CustomFieldsForm from '../CustomFieldsForm';
import customFieldsApi from 'api/requests/customFields';
import { useEvent } from 'context/events/Event';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function CustomFieldsSection({ data, loading, onBack, toast, onSuccess }) {
    const { event } = useEvent();
    const [modalVisible, setModalVisible] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [edit, setEdit] = useState(false);

    function handleToggleForm() {
        if (!data?.length) {
            setModalVisible(true);
        } else {
            setEdit(data);
            setModalVisible(true);
        }
    }

    function handleFormSuccess() {
        setModalVisible(false);
        setEdit(false);
        onSuccess();
    }

    async function handleDeleteField() {
        if (!fieldToDelete) return;
        setDeleting(true);

        const remaining = data
            .filter(f => f.id !== fieldToDelete.id)
            .map(f => ({
                label: f.label,
                type: f.type,
                required: f.required,
                options: f.options || []
            }));

        const payload = {
            event_id: event?.id,
            custom_fields: remaining
        };

        try {
            await customFieldsApi.update(payload);
            setFieldToDelete(null);
            toast.success('Campo excluído com sucesso!', { position: 'top-center' });
            onSuccess();
        } catch (error) {
        } finally {
            setDeleting(false);
        }
    }

    const count = data?.length || 0;

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Plus size={20} color={panelAccent(colors.occasionalPurple)} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Campos personalizados</SectionTitle>
                                {count > 0 && <CountBadge>{count}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                Colete informações extras dos participantes durante a inscrição.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={handleToggleForm}
                        >
                            {data?.length
                                ? <Edit color={colors.neutralLight} size={16} />
                                : <PlusSquare color={colors.neutralLight} size={16} />
                            }
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                {data?.length ? 'Editar formulário' : 'Adicionar campos'}
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : !count ? (
                    <EmptyState>
                        <EmptyIcon>
                            <Plus size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhum campo personalizado criado ainda.
                        </Text>
                        <EmptyHint>
                            Adicione campos para coletar dados extras na inscrição.
                        </EmptyHint>
                    </EmptyState>
                ) : (
                    <FieldsGrid>
                        {data?.map(el => (
                            <CustomFieldCard
                                data={el}
                                key={el?.id}
                                onDelete={(field) => setFieldToDelete(field)}
                            />
                        ))}
                    </FieldsGrid>
                )}
            </SectionCard>

            {modalVisible &&
                <CustomFieldsForm
                    visible={modalVisible}
                    isEdit={edit}
                    toast={toast}
                    onClose={() => {
                        setEdit(false);
                        setModalVisible(false);
                    }}
                    onSuccess={handleFormSuccess}
                />
            }

            {fieldToDelete && (
                <Modal isOpen={!!fieldToDelete} onClose={() => setFieldToDelete(null)}>
                    <Text name='highlight'>Excluir campo</Text>
                    <Text>
                        Tem certeza que deseja excluir o campo <b>"{fieldToDelete?.label}"</b>?
                    </Text>
                    {deleting ? (
                        <LoaderWrap style={{ minHeight: '40px' }}>
                            <Loader />
                        </LoaderWrap>
                    ) : (
                        <ModalActions>
                            <Button variant='light' category='roundedBorded' onClick={() => setFieldToDelete(null)}>
                                Cancelar
                            </Button>
                            <Button category='roundedBorded' variant='lightBlue' onClick={handleDeleteField}>
                                Excluir
                            </Button>
                        </ModalActions>
                    )}
                </Modal>
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.neutralLight};
    background: ${panelAccent(colors.secondaryPurple)};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;

const FieldsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;

    > div { width: 100%; }

    @media screen and (min-width: ${breakpoints.lg}) {
        > div { width: calc(33.33% - 8px); }
    }
`;

const ModalActions = styled(Column)`
    margin-top: 20px;
    flex-direction: row;

    button:nth-child(2) {
        margin-left: 10px;
    }
`;
